import { format } from 'date-fns'
import BannerSection from '@/pages/dashboard/widgets/BannerSection'
import { Skeleton } from '@/components/ui/skeleton'
import useGetAvailableToSpend from '@/services/api/cash-flow/useGetAvailableToSpend'
import CardErrorState from '@/pages/dashboard/widgets/CardErrorState'

/** Full comma-separated dollars, no decimals — matches the ticket's own "$22,000" example. */
function formatCurrency(value: number): string {
  const sign = value < 0 ? '-' : ''
  return `${sign}$${Math.round(Math.abs(value)).toLocaleString('en-US')}`
}

export default function AvailableToSpendCard() {
  const { data, status, refetch } = useGetAvailableToSpend()

  const isOvercommitted =
    status === 'success' && !!data && data.net_available !== undefined && data.net_available < 0

  return (
    <BannerSection
      label="Available funds as of today"
      right={
        isOvercommitted && (
          <span className="inline-flex w-fit items-center rounded-md bg-red-default px-2 py-0.5 text-xs font-bold text-white">
            Overcommitted
          </span>
        )
      }
      className={isOvercommitted ? 'bg-red-lowest' : ''}
    >
      {status === 'pending' && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-4 w-56" />
          <div className="text-[11px] text-txt-secondary">
            Loading — each card resolves on its own
          </div>
        </div>
      )}

      {status === 'error' && <CardErrorState onRetry={() => refetch()} />}

      {status === 'success' && data && (
        <div className="flex flex-col gap-1">
          <div className="font-raleway text-[34px] font-bold leading-none tabular-nums text-txt-primary tablet:text-[38px]">
            {formatCurrency(data.balance)}
          </div>

          {!data.next_income && (
            <div className="text-sm text-txt-secondary">No income expected</div>
          )}

          {data.next_income && data.next_income.days_away === 1 && (
            <div className="text-sm text-txt-secondary">
              Next income of {formatCurrency(data.next_income.amount)} expected tomorrow
            </div>
          )}

          {data.next_income &&
            data.next_income.days_away > 1 &&
            data.required !== undefined &&
            data.net_available !== undefined && (
              <div className="text-sm text-txt-secondary">
                Until next estimated income of {formatCurrency(data.next_income.amount)} in{' '}
                {data.next_income.days_away} days you&apos;ll need {formatCurrency(data.required)} ·{' '}
                <span
                  className={`font-semibold ${data.net_available < 0 ? 'text-[hsl(356,72%,47%)]' : 'text-[hsl(138,71%,23%)]'}`}
                >
                  Net available {formatCurrency(data.net_available)}
                </span>
              </div>
            )}

          {isOvercommitted && data.next_income && (
            <div className="mt-0.5 text-sm font-medium text-[hsl(356,72%,47%)]">
              {formatCurrency(Math.abs(data.net_available))} of committed money has already been spent.
              Cover it before the {format(new Date(data.next_income.date), 'do')}.
            </div>
          )}
        </div>
      )}
    </BannerSection>
  )
}
